/**
 * ReceivingScanInput — compact inventory-app scan field for the receiving card.
 *
 * Inventory-app styling (NOT the chrome-free production station style): a normal
 * bordered shadcn Input with a barcode glyph. USB wedge scanners type the SKU
 * string then send Enter; there is no separate barcode column in the schema, so
 * the SKU code string IS the barcode (BLU-584).
 *
 * Behaviour:
 * - Buffered, uncontrolled input (matches QRScannerInput mechanics) — the value
 *   accumulates as the scanner types, then Enter commits.
 * - `data-scanner-input` so `useScannerAutoFocus` can steal focus back here when
 *   no other input is focused.
 * - Auto-clears after every commit and refocuses, ready for the next scan.
 * - Plays a success or error beep via `@/lib/sounds` based on what the parent's
 *   `onScan` reports; the parent owns the toast + allocation logic.
 */
import { useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Barcode } from "lucide-react";
import { playSuccessBeep, playErrorBeep } from "@/lib/sounds";

/** Result of a scan attempt, reported back by the parent. */
export type ReceivingScanResult = "allocated" | "fully-allocated" | "not-found";

interface ReceivingScanInputProps {
  /** Resolve a scanned SKU against the order. Return whether a unit was
   *  allocated ("allocated"), the row is already at its remaining cap
   *  ("fully-allocated"), or no matching row exists ("not-found"). */
  onScan: (code: string) => ReceivingScanResult;
  disabled?: boolean;
}

/** Guard against pathological paste/scan payloads. */
const MAX_SCAN_LENGTH = 256;

export function ReceivingScanInput({ onScan, disabled = false }: ReceivingScanInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = useCallback(() => {
    const raw = inputRef.current?.value.trim() ?? "";
    if (!raw || disabled) return;

    const code = raw.slice(0, MAX_SCAN_LENGTH);
    const result = onScan(code);

    if (result === "allocated") {
      playSuccessBeep();
    } else {
      playErrorBeep();
    }

    // Clear + refocus for the next scan regardless of outcome.
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus({ preventScroll: true });
    }
  }, [onScan, disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // USB scanners terminate the payload with Enter.
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
      }
    },
    [commit],
  );

  return (
    <div className="relative w-full max-w-sm">
      <Barcode
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        data-scanner-input
        ref={inputRef}
        type="text"
        role="search"
        aria-label="Scan a SKU to receive"
        placeholder="Scan or type a SKU…"
        disabled={disabled}
        className="h-9 pl-9 font-mono text-sm"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}
